import {
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  Tab,
  Tabs,
  Typography
} from '@material-ui/core'
import SearchIcon from '@material-ui/icons/Search'
import clsx from 'clsx'
import {
  CircleArrowRight,
  CirclePlus,
  MessagesSquare,
  UsersRound
} from 'lucide-react'
import {
  forwardRef,
  useEffect,
  useImperativeHandle
} from 'react'
import { InputFieldText } from '../InputFieldText'
import { useStyles } from './style'

import { ChatModal } from '../../components/ChatModal'
import TableRowSkeleton from '../../components/TableRowSkeleton'
import { useChatLogic } from '../../hooks/useChatLogic'
import ChatDrawerMessages from '../ChatDrawerMessages'
import UserAvatar from '../UserAvatar'

const ChatDrawer = forwardRef((props, ref) => {
  const { open, setOpen, initialChatType, initialChatData } = props
  const classes = useStyles()

  const {
    tabValue,
    showDialog,
    selectedUser,
    selectedChat,
    showMessages,
    loading,
    hasMore,
    searchParam,
    users,
    chats,
    loadedAvatars,
    avatarLoadingStates,
    usersStatus,
    currentUser,
    actions,
    helpers
  } = useChatLogic(open, initialChatType, initialChatData)

  useImperativeHandle(ref, () => ({
    openSpecificChat: (userData, chatData) => {
      actions.setTabValue(0)
      actions.handleOpenChat(userData)
      setOpen(true)
      actions.setShowMessages(true)
    },
    openSpecificGroupChat: chatData => {
      actions.setTabValue(1)
      actions.handleOpenGroupChat(chatData)
      setOpen(true)
      actions.setShowMessages(true)
    }
  }))

  const handleTabChange = (event, newValue) =>
    actions.handleTabChange(event, newValue)
  const handleSearch = event => actions.handleSearch(event)
  const handleBackToList = () => actions.handleBackToList()
  const handleNewGroup = () => actions.setShowDialog(true)

  const handleLoadNewChat = data => {
    actions.setSelectedChat(data)
    actions.setSelectedUser(null)
    actions.setShowMessages(true)
    actions.chatDispatch({ type: 'UPDATE_ITEM', payload: data })
  }

  const handleScroll = e => {
    if (!hasMore || loading) return
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      actions.setPageNumber(prevState => prevState + 1)
    }
  }

  const StatusBadge = ({ isOnline }) => (
    <div
      className={clsx(
        classes.statusBadge,
        isOnline ? classes.onlineBadge : classes.offlineBadge
      )}
    />
  )

  const UnreadCounter = ({ count }) => {
    if (count <= 0) return null
    return <Chip label={count} className={classes.badgeCountMessage} />
  }

  const ChatDrawerAvatarWrapper = ({ user, className, onClick }) => {
    const isGroup = Boolean(user?.title)
    const displayName = isGroup ? user.title : (user?.name || '')
    const userId = user?.id
    const avatarData = loadedAvatars[userId]

    useEffect(() => {
      if (isGroup) return
      if (userId && !avatarData && !avatarLoadingStates[userId]) {
        actions.loadUserAvatar(userId)
      }
    }, [userId, avatarData, isGroup])

    return (
      <UserAvatar
        name={displayName}
        src={avatarData?.url}
        isGroup={isGroup}
        className={className}
        onClick={onClick}
      />
    )
  }

  const chatMessagesContent = (
    <ChatDrawerMessages
      user={selectedUser}
      chat={selectedChat}
      onClose={handleBackToList}
    />
  )

  const userListContent = (
    <>
      <Box className={`${classes.headerChat} + ${classes.header}`}>
        <Typography variant="body2" component="span">
          Chat Interno
        </Typography>
        <IconButton edge="end" color="inherit" onClick={() => setOpen(false)}>
          <CircleArrowRight />
        </IconButton>
      </Box>

      <Tabs
        value={tabValue}
        onChange={handleTabChange}
        className={classes.tabs}
        indicatorColor="primary"
        textColor="primary"
        variant="fullWidth"
      >
        <Tab
          className={classes.tab}
          label={
            <div className={classes.tabLabel}>
              <MessagesSquare className={classes.tabIcon} />
              <span>Team</span>
            </div>
          }
        />
        <Tab
          className={classes.tab}
          label={
            <div className={classes.tabLabel}>
              <UsersRound className={classes.tabIcon} />
              <span>Grupos</span>
            </div>
          }
        />
      </Tabs>

      <div className={classes.form}>
        <InputFieldText
          size="small"
          name={'searchParam'}
          placeholder="Pesquisar"
          onChange={handleSearch}
          value={searchParam}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start" className={classes.searchIcon}>
                <SearchIcon />
              </InputAdornment>
            )
          }}
        />
        {tabValue === 1 && currentUser?.profile === 'admin' && (
          <div className={classes.newGroupButtonContainer}>
            <Button
              variant="contained"
              onClick={handleNewGroup}
              className={classes.newGroupButton}
            >
              <CirclePlus />
            </Button>
          </div>
        )}
      </div>

      <List className={classes.chatList} onScroll={handleScroll}>
        {(tabValue === 0 ? users : chats).map(item => {
          const unreads = helpers.getUnreadCount(item)
          const hasUnread = unreads > 0
          const isUser = tabValue === 0

          const isOnline = !!usersStatus[String(item.id)]

          return (
            <ListItem
              button
              key={item.id}
              className={`${classes.userItem} ${hasUnread ? classes.unreadItem : ''}`}
              onClick={() =>
                isUser
                  ? actions.handleOpenChat(item)
                  : actions.handleOpenGroupChat(item)
              }
            >
              <div style={{ position: 'relative' }}>
                <ChatDrawerAvatarWrapper user={item} className={classes.avatar} />
                {isUser && unreads === 0 && <StatusBadge isOnline={isOnline} />}
              </div>
              <UnreadCounter count={unreads} />
              <ListItemText
                className={classes.userItemText}
                primary={
                  <div className={classes.boxTitleNewChat}>
                    <span
                      className={`${classes.titleNewChat} ${hasUnread ? classes.titleNewChatBold : ''}`}
                    >
                      {item.name || item.title}
                    </span>
                  </div>
                }
                secondary={
                  item.lastMessage ? (
                    <span className={classes.lastMessage}>
                      {item.lastMessage}
                    </span>
                  ) : null
                }
                primaryTypographyProps={{ component: 'div' }}
                secondaryTypographyProps={{ component: 'div' }}
              />
            </ListItem>
          )
        })}
        {loading && (
          <div className={classes.loadingContainer}>
            <TableRowSkeleton columns={1} />
          </div>
        )}
      </List>
    </>
  )

  const closedDrawerContent = (
    <div className={classes.closedDrawerContent}>
      <div className={classes.avatarContainer} onClick={() => setOpen(true)}>
        <IconButton className={classes.openDrawerButton}>
          <MessagesSquare size={24} />
        </IconButton>
      </div>

      {users.map(user => {
        const unreads = helpers.getUnreadCount(user)

        const isOnline =
          !!usersStatus[user.id] ||
          !!usersStatus[String(user.id)] ||
          !!usersStatus[Number(user.id)]
        return (
          <div
            key={`user-${user.id}`}
            className={classes.avatarContainer}
            onClick={() => {
              actions.handleOpenChat(user)
              setOpen(true)
              actions.setTabValue(0)
            }}
          >
            <div style={{ position: 'relative' }}>
              <ChatDrawerAvatarWrapper user={user} className={classes.avatar} />
              {unreads === 0 && <StatusBadge isOnline={isOnline} />}
            </div>
            {unreads > 0 && (
              <div className={classes.miniUnreadBadge}>
                <span>{unreads}</span>
              </div>
            )}
          </div>
        )
      })}

      <Divider className={classes.divider} />

      {chats.map(chat => {
        const unreads = helpers.getUnreadCount(chat)
        return (
          <div
            key={`chat-${chat.id}`}
            className={classes.avatarContainer}
            onClick={() => {
              actions.handleOpenGroupChat(chat)
              setOpen(true)
              actions.setTabValue(1)
            }}
          >
            <ChatDrawerAvatarWrapper user={chat} className={classes.circle} />
            {unreads > 0 && (
              <div className={classes.miniUnreadBadge}>{unreads}</div>
            )}
          </div>
        )
      })}
    </div>
  )

  return (
    <>
      <ChatModal
        open={showDialog}
        chat={selectedChat}
        handleLoadNewChat={handleLoadNewChat}
        handleClose={() => actions.setShowDialog(false)}
        user={currentUser}
      />
      <Drawer
        variant="permanent"
        anchor="right"
        open={open}
        className={`${open ? classes.drawer : classes.drawerClosed} ${classes.drawerRoot}`}
        classes={{
          paper: clsx(classes.drawerPaper, !open && classes.drawerPaperClose)
        }}
      >
        {open
          ? showMessages
            ? chatMessagesContent
            : userListContent
          : closedDrawerContent}
      </Drawer>
    </>
  )
})

export default ChatDrawer
